import { inject, Injectable } from '@angular/core';
import { filter, map, of, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { EffectsBase, withCacheGate } from '@ps/core-data-access';
import { markCached } from '@ps/utils';
import { ImpersonationActions } from '@ps/auth-data-access';

import {
  REFERENCE_CACHE_TTL_MS,
  SURVEY_TRANSACTION_STATUSES_CACHE_TTL_MS,
} from '../../utils/cache/survey-cache.constants';
import { SurveyReferenceService } from '../../services/survey-reference/survey-reference.service';
import {
  SurveyReferenceActions,
  SurveyReferenceApiActions,
} from './survey-reference.actions';
import {
  selectBuyerPmCachedAt,
  selectOperatorPmCachedAt,
  selectTemplatesCachedAt,
  selectTransactionStatusesCachedAt,
} from './survey-reference.selectors';

@Injectable()
export class SurveyReferenceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _referenceService = inject(SurveyReferenceService);

  public loadTemplates$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyReferenceActions.loadTemplates),
      withCacheGate(
        this._store.select(selectTemplatesCachedAt),
        REFERENCE_CACHE_TTL_MS,
      ),
      switchMap(({ hasImpersonatedBuyer, companyId }) =>
        this._referenceService
          .getTemplates({ hasImpersonatedBuyer, companyId })
          .pipe(
            map((templates) =>
              SurveyReferenceApiActions.loadTemplatesSuccess({
                templates,
                cachedAt: markCached(),
                companyId,
              }),
            ),
            this.psCatchError((err) =>
              of(
                SurveyReferenceApiActions.loadTemplatesFailure({
                  error: err.message || 'Failed to load templates',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  public loadBuyerManagers$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyReferenceActions.loadBuyerManagers),
      withCacheGate(
        this._store.select(selectBuyerPmCachedAt),
        REFERENCE_CACHE_TTL_MS,
      ),
      switchMap(({ companyId }) =>
        this._referenceService.getBuyerManagers(companyId).pipe(
          map((managers) =>
            SurveyReferenceApiActions.loadBuyerManagersSuccess({
              managers,
              cachedAt: markCached(),
              companyId,
            }),
          ),
          this.psCatchError((err) =>
            of(
              SurveyReferenceApiActions.loadBuyerManagersFailure({
                error: err.message || 'Failed to load buyer project managers',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public loadOperatorManagers$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyReferenceActions.loadOperatorManagers),
      withCacheGate(
        this._store.select(selectOperatorPmCachedAt),
        REFERENCE_CACHE_TTL_MS,
      ),
      switchMap(() =>
        this._referenceService.getOperatorManagers().pipe(
          map((managers) =>
            SurveyReferenceApiActions.loadOperatorManagersSuccess({
              managers,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError((err) =>
            of(
              SurveyReferenceApiActions.loadOperatorManagersFailure({
                error:
                  err.message || 'Failed to load operator project managers',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public loadTransactionStatuses$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyReferenceActions.loadTransactionStatuses),
      withCacheGate(
        this._store.select(selectTransactionStatusesCachedAt),
        SURVEY_TRANSACTION_STATUSES_CACHE_TTL_MS,
      ),
      switchMap(() =>
        this._referenceService.getTransactionStatuses().pipe(
          map((statuses) =>
            SurveyReferenceApiActions.loadTransactionStatusesSuccess({
              statuses,
              cachedAt: markCached(),
            }),
          ),
          this.psCatchError((err) =>
            of(
              SurveyReferenceApiActions.loadTransactionStatusesFailure({
                error: err.message || 'Failed to load transaction statuses',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * Only reloads when the operator has impersonated a buyer. The reducer's
   * own reset already empties templates/PMs on disconnect, and the plain
   * operator view doesn't need them.
   */
  public reloadOnImpersonationContextSettled$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ImpersonationActions.contextSettled),
      filter(({ hasImpersonatedBuyer }) => hasImpersonatedBuyer),
      switchMap(({ companyId }) =>
        of(
          SurveyReferenceActions.loadTemplates({
            hasImpersonatedBuyer: true,
            companyId,
          }),
          SurveyReferenceActions.loadBuyerManagers({ companyId }),
          SurveyReferenceActions.loadOperatorManagers(),
        ),
      ),
    ),
  );

  public downloadCombinedSTReport$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyReferenceActions.downloadCombinedSTReport),
      switchMap(
        ({ cmp, userType, surveys, statuses, includeTestTransactions }) =>
          this._referenceService
            .getCombinedSurveyTrafficReport({
              cmp,
              userType,
              surveys,
              statuses,
              includeTestTransactions,
            })
            .pipe(
              map(() =>
                SurveyReferenceApiActions.downloadCombinedSTReportSuccess(),
              ),
              this.psCatchError((err) =>
                of(
                  SurveyReferenceApiActions.downloadCombinedSTReportFailure({
                    error:
                      err.message ||
                      'Failed to download combined survey traffic report.',
                  }),
                ),
              ),
            ),
      ),
    ),
  );
}
